import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { checkAppVersion, APP_VERSION } from '../services/appVersion';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}22 0%, ${props => props.theme.colors.secondary}22 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  padding: ${props => props.theme.spacing.lg};
`;

const Card = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  padding: ${props => props.theme.spacing.xl};
  max-width: 400px;
  width: 100%;
  text-align: center;
`;

const IconContainer = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.info} 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${props => props.theme.spacing.lg};

  svg {
    width: 40px;
    height: 40px;
    color: white;
  }
`;

const Title = styled.h2`
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.fontSize.xxl};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const Message = styled.p`
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.fontSize.base};
  line-height: 1.6;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const VersionInfo = styled.div`
  background: ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};

  span {
    display: block;
    margin: ${props => props.theme.spacing.xs} 0;
  }
`;

const UpdateButton = styled.a`
  display: block;
  width: 100%;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.info} 100%);
  color: white;
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`;

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7,10 12,15 17,10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

export const ForceUpdate = ({ children }) => {
  const [versionCheck, setVersionCheck] = useState({
    loading: true,
    needsUpdate: false,
    updateMessage: '',
    storeUrl: '',
    minVersion: ''
  });

  useEffect(() => {
    const checkVersion = async () => {
      const result = await checkAppVersion();
      setVersionCheck({
        loading: false,
        needsUpdate: result.needsUpdate,
        updateMessage: result.updateMessage || '',
        storeUrl: result.storeUrl || '',
        minVersion: result.minVersion || ''
      });
    };

    checkVersion();
  }, []);

  // Mientras carga, mostrar los children normalmente
  if (versionCheck.loading) {
    return children;
  }

  // Si necesita actualización, bloquear la app
  if (versionCheck.needsUpdate) {
    return (
      <Overlay>
        <Card>
          <IconContainer>
            <DownloadIcon />
          </IconContainer>

          <Title>Actualización Requerida</Title>

          <Message>{versionCheck.updateMessage}</Message>

          <VersionInfo>
            <span><strong>Tu versión:</strong> {APP_VERSION}</span>
            <span><strong>Versión requerida:</strong> {versionCheck.minVersion}</span>
          </VersionInfo>

          <UpdateButton
            href={versionCheck.storeUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Actualizar Ahora
          </UpdateButton>
        </Card>
      </Overlay>
    );
  }

  // Si no necesita actualización, renderizar la app normalmente
  return children;
};
